<script setup>
import { ref, computed, reactive, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { required, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useAccount } from 'dashboard/composables/useAccount';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const flows = useMapGetter('flows/getFlows');
const uiFlags = useMapGetter('flows/getUIFlags');

const searchQuery = ref('');
const selectedFlow = ref({});
const createDialogRef = ref(null);
const deleteDialogRef = ref(null);

const formState = reactive({ name: '', description: '' });

const v$ = useVuelidate(
  {
    name: {
      required: helpers.withMessage(
        () => t('FLOWS.FORM.ERRORS.NAME'),
        required
      ),
    },
  },
  formState
);

const tableHeaders = computed(() => [
  t('FLOWS.LIST.TABLE_HEADER.NAME'),
  t('FLOWS.LIST.TABLE_HEADER.STATUS'),
  t('FLOWS.LIST.TABLE_HEADER.ACTIONS'),
]);

const filteredFlows = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return flows.value;
  return flows.value.filter(flow => flow.name?.toLowerCase().includes(query));
});

const openCreateDialog = () => {
  Object.assign(formState, { name: '', description: '' });
  v$.value.$reset();
  createDialogRef.value.open();
};

const openDeleteDialog = flow => {
  selectedFlow.value = flow;
  deleteDialogRef.value.open();
};

const handleCreate = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const flow = await store.dispatch('flows/create', { ...formState });
    createDialogRef.value.close();
    useAlert(t('FLOWS.ADD.API.SUCCESS_MESSAGE'));
    if (flow?.id) {
      router.push(accountScopedRoute('flows_edit', { flowId: flow.id }));
    }
  } catch (error) {
    useAlert(t('FLOWS.ADD.API.ERROR_MESSAGE'));
  }
};

const confirmDeletion = async () => {
  try {
    await store.dispatch('flows/delete', selectedFlow.value.id);
    useAlert(t('FLOWS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('FLOWS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    deleteDialogRef.value.close();
    selectedFlow.value = {};
  }
};

onMounted(() => {
  store.dispatch('flows/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('FLOWS.LIST.LOADING')"
    :no-records-found="!flows.length"
    :no-records-message="t('FLOWS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="t('FLOWS.HEADER')"
        :description="t('FLOWS.DESCRIPTION')"
        :search-placeholder="t('FLOWS.SEARCH_PLACEHOLDER')"
        feature-name="agent_bots"
      >
        <template v-if="flows.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('FLOWS.COUNT', { n: flows.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('FLOWS.ADD.TITLE')"
            size="sm"
            @click="openCreateDialog"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredFlows"
        :no-data-message="
          searchQuery ? t('FLOWS.NO_RESULTS') : t('FLOWS.LIST.404')
        "
      >
        <template #row="{ items }">
          <BaseTableRow v-for="flow in items" :key="flow.id" :item="flow">
            <template #default>
              <BaseTableCell class="max-w-0">
                <router-link
                  :to="accountScopedRoute('flows_edit', { flowId: flow.id })"
                  class="text-body-main text-n-blue-text hover:underline truncate block"
                >
                  {{ flow.name }}
                </router-link>
                <span
                  v-if="flow.description"
                  class="text-body-main text-n-slate-11 block truncate"
                >
                  {{ flow.description }}
                </span>
              </BaseTableCell>

              <BaseTableCell class="w-32">
                <span
                  class="text-xs text-n-slate-11 bg-n-slate-3 rounded-md py-0.5 px-1.5"
                >
                  {{ t(`FLOWS.STATUS.${flow.status?.toUpperCase()}`) }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end" class="w-24">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-tooltip.top="t('FLOWS.EDIT.BUTTON_TEXT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="
                      router.push(
                        accountScopedRoute('flows_edit', { flowId: flow.id })
                      )
                    "
                  />
                  <Button
                    v-tooltip.top="t('FLOWS.DELETE.BUTTON_TEXT')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="openDeleteDialog(flow)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <Dialog ref="createDialogRef" type="edit" :title="t('FLOWS.ADD.TITLE')">
      <form class="flex flex-col gap-4" @submit.prevent="handleCreate">
        <Input
          id="flow-name"
          v-model="formState.name"
          :label="t('FLOWS.FORM.NAME.LABEL')"
          :placeholder="t('FLOWS.FORM.NAME.PLACEHOLDER')"
          :message="v$.name.$error ? v$.name.$errors[0]?.$message : ''"
          :message-type="v$.name.$error ? 'error' : 'info'"
          @blur="v$.name.$touch()"
        />
        <TextArea
          id="flow-description"
          v-model="formState.description"
          :label="t('FLOWS.FORM.DESCRIPTION.LABEL')"
          :placeholder="t('FLOWS.FORM.DESCRIPTION.PLACEHOLDER')"
        />
        <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
          <Button
            faded
            slate
            type="button"
            :label="t('FLOWS.FORM.CANCEL')"
            @click="createDialogRef.close()"
          />
          <Button
            type="submit"
            :label="t('FLOWS.FORM.CREATE')"
            :is-loading="uiFlags.isCreating"
            :disabled="v$.$invalid"
          />
        </div>
      </form>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('FLOWS.DELETE.CONFIRM.TITLE')"
      :description="
        t('FLOWS.DELETE.CONFIRM.MESSAGE', { name: selectedFlow.name })
      "
      :is-loading="uiFlags.isDeleting"
      :confirm-button-label="t('FLOWS.DELETE.CONFIRM.YES')"
      :cancel-button-label="t('FLOWS.DELETE.CONFIRM.NO')"
      @confirm="confirmDeletion"
    />
  </SettingsLayout>
</template>
